import { useMemo } from 'react';
import { ErrorBoundary, ErrorBoundaryProps } from '@/components/ErrorBoundary';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { VizContainerContentError } from '../content-error/VizContainerContentError';
import { useVizContainerErrorsAndNotices } from '../hooks';
import { VizContainerNoticesProps } from '../notices';

export type VizContainerVizErrorBoundaryResetKeysFn = (
  defaultErrorBoundaryResetKeys: NonNullable<ErrorBoundaryProps['resetKeys']>
) => NonNullable<ErrorBoundaryProps['resetKeys']>;

export interface VizContainerVizErrorBoundaryProps extends Pick<VizContainerNoticesProps, 'errors'> {
  /**
   * Keys that will reset the error boundary's error state when they change. Used to avoid an error boundary being
   * stuck in a permanent error state.
   *
   * This prop supports three different ways of setting the error boundary reset keys:
   * - `undefined` (default behavior): Reset keys are based on the `children` prop and the `refreshKey` from
   *   `useTimeRange`.
   * - `any[]`: Explicitly sets the reset keys. Useful in cases where you do not want to include the default values.
   *    Note that setting this to an empty array will disable resetting functionality. Use `undefined` if you want
   *    default behavior.
   * - `function`: Callback function that provides the default reset keys as an argument and returns the reset keys
   *   that will be used. This is useful in mixed cases where you want to combine the default reset keys with some
   *   additional keys.
   */
  errorBoundaryResetKeys?: ErrorBoundaryProps['resetKeys'] | VizContainerVizErrorBoundaryResetKeysFn;
  /**
   * Context aware data visualization to render.
   */
  children: React.ReactNode;
}

/**
 * Component for wrapping context-aware data visualizations rendered inside a `VizContainer` to provide consistent error
 * handling. It currently provides the following functionality:
 * - Error boundary that automatically resets in common use cases (`children` prop, `refreshKey` from the
 *   `TimeRangeProvider`). You may modify reset keys via the `errorBoundaryResetKeys` prop.
 * - Rendering prominent errors instead of the visualization when the error state is "critical," which indicates the
 *   chart is unlikely to be able to render.
 */
export const VizContainerVizErrorBoundary = ({
  children,
  errorBoundaryResetKeys: errorBoundaryResetKeysProp,
  errors: errorsProp,
}: VizContainerVizErrorBoundaryProps) => {
  const {
    errors: { errors, errorState },
  } = useVizContainerErrorsAndNotices({ errors: errorsProp });

  // To reset error state when a new query is issued or time range is refreshed
  const { refreshKey } = useTimeRange();

  const errorBoundaryResetKeys = useMemo(() => {
    if (Array.isArray(errorBoundaryResetKeysProp)) {
      return errorBoundaryResetKeysProp;
    }

    const defaultErrorBoundaryResetKeys = [children, refreshKey];

    if (typeof errorBoundaryResetKeysProp === 'function') {
      return errorBoundaryResetKeysProp(defaultErrorBoundaryResetKeys);
    }

    return defaultErrorBoundaryResetKeys;
  }, [children, errorBoundaryResetKeysProp, refreshKey]);

  return (
    <ErrorBoundary resetKeys={errorBoundaryResetKeys} FallbackComponent={VizContainerContentError}>
      {errorState === 'critical' ? <VizContainerContentError error={errors} /> : children}
    </ErrorBoundary>
  );
};
